import { useRoute } from "@react-navigation/native";
import { FC, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useSelector } from "react-redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tReduxState } from "src/redux/types";
import { tUserPath } from "src/redux/user/types";
import { kPhonePattern } from "src/util/constants";
import { formatPhoneNumber } from "src/util/gFormatting";
import { Spacer } from ".";
import { ValidatedTextFormInput } from "./FormInputs";
import { useFetchAdminUserSettings } from "../redux/AdminUser/actions";
import { kRoute } from "src/kRoute";
import { sendInfo } from "src/redux/AdminSettings/actions";
import { useAppDispatch } from "src/redux";
import { useChangeEmailModal } from "src/hooks/modals";
import { useIsMobile } from "src/hooks/ui";
import { PdButton, PdButtonLabel } from "./forms/button";
import { PdStack, PdText } from "./layout";
import { formatCurrency } from "tmt_api/src/util/formatters";

const userNameSelector = ({ AdminUserReducer }: tReduxState) => AdminUserReducer.userName;
const userTitleSelector = ({ AdminUserReducer }: tReduxState) => AdminUserReducer.userTitle;
const emailSelector = ({ AdminUserReducer }: tReduxState) => AdminUserReducer.email;
const phoneSelector = ({ AdminUserReducer }: tReduxState) => formatPhoneNumber(AdminUserReducer.phone);
type tAdminBrandSettingsParams = {
  params: { brandId?: string; retailerId?: string; repId?: string; companyType?: "brands" | "boutiques" | "reps" };
  key: string;
  name: string;
};

const AdminRetailerSection: FC = () => {
  const rAdminSettings = useSelector((state) =>
    state.AdminSettingReducer.type === "retailer" ? state.AdminSettingReducer : undefined,
  );

  if (!rAdminSettings) {
    return null;
  }

  return (
    <View>
      <PdText>Available in Terms</PdText>
      <PdText>{formatCurrency(rAdminSettings.availableBalance ?? 0)}</PdText>
    </View>
  );
};

const AdminUserPanel: FC<{ isValid?: { [key: string]: boolean } }> = ({ isValid: _myIsValid }) => {
  const rAdmin = useSelector(({ AdminSettingReducer }) => AdminSettingReducer);
  const rUser = useSelector(({ AdminUserReducer }) => AdminUserReducer);
  const route = useRoute<tAdminBrandSettingsParams>();
  let name: "brands" | "boutiques" | "reps" | undefined;
  let routeId: string | undefined;
  if (route.name === kRoute.P06_09) {
    name = "brands";
    routeId = route?.params?.brandId;
  } else if (route.name === kRoute.P06_10) {
    name = "boutiques";
    routeId = route?.params?.retailerId;
  } else if (route.name === kRoute.P06_11) {
    name = "reps";
    routeId = route?.params?.repId;
  }

  useFetchAdminUserSettings(routeId, name);
  const dispatch = useAppDispatch();
  const rLoading = useSelector((state) => state.LoadingReducer.userFetching);
  const [sIsValid, setIsValid] = useState<{ [key: string]: boolean }>({});
  const rCompanyType = useSelector(({ AdminUserReducer }) => AdminUserReducer.accountType);
  const changeEmailModal = useChangeEmailModal();
  const isMobile = useIsMobile();

  const handleOnPressChangeEmail = () => {
    changeEmailModal.openAsync({ accountType: rUser.accountType, user: rUser });
  };

  return (
    <View style={styles.P09_PageContainer}>
      <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
        <View style={styles.rowColumn}>
          <Text style={styles.gFormHeader}>Personal Account Information</Text>
          <Text>This information is private and will not be shared</Text>
        </View>
      </View>
      <View style={styles.formControl}>
        <View style={styles.gFormRowNoMargin}>
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "ADMIN_UPDATE_USER", userPart: { userName: "" } }}
            dispatchPath="userPart.userName"
            selectorFunction={userNameSelector}
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Your full name",
            }}
            uniqueName="userName"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="Primary User Name*"
            rules={{ required: { message: "Required" } }}
            loading={rLoading}
          />
          <Spacer />
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "ADMIN_UPDATE_USER", userPart: { userTitle: "" } }}
            dispatchPath="userPart.userTitle"
            selectorFunction={userTitleSelector}
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Buyer & Owner",
            }}
            uniqueName="userTitle"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="User Role*"
            rules={{ required: { message: "Required" } }}
            loading={rLoading}
          />
        </View>
        <View style={styles.gFormRowNoMargin}>
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "ADMIN_UPDATE_USER", userPart: { email: "" } }}
            dispatchPath="userPart.email"
            textInputProps={{
              style: styles.gLinkBankInputGray,
              placeholder: "User Email",
              editable: false,
            }}
            selectorFunction={emailSelector}
            uniqueName="email"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="Account Email*"
            rules={{
              required: { message: "Required" },
              pattern: {
                pattern:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: "Please enter valid email",
              },
            }}
            loading={rLoading}
          />
          <Spacer />
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "ADMIN_UPDATE_USER", userPart: { phone: "" } }}
            dispatchPath="userPart.phone"
            selectorFunction={phoneSelector}
            uniqueName="phone"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="Phone Number*"
            loading={rLoading}
            rules={{
              required: { message: "Required" },
              pattern: {
                pattern: kPhonePattern,
                message: "Please enter valid Phone Number",
              },
            }}
            textInputProps={{
              autoCompleteType: "tel",
              placeholder: "(XXX) XXX-XXXX",
              style: styles.gLinkBankInput,
            }}
          />
        </View>
        {rCompanyType === "retailer" && <AdminRetailerSection />}
        <PdStack
          isReversed={isMobile}
          direction={isMobile ? "vertical" : "horizontal"}
          style={[styles.mt6, styles.justifyBetween]}
        >
          <PdButton variant="outline" onPress={handleOnPressChangeEmail} style={styles.minWMd}>
            <PdButtonLabel>Change Email</PdButtonLabel>
          </PdButton>
          {rCompanyType !== "brand" && (
            <PdButton onPress={() => dispatch(sendInfo(rAdmin, rUser))} style={styles.minWMd}>
              <PdButtonLabel>Save</PdButtonLabel>
            </PdButton>
          )}
        </PdStack>
      </View>
    </View>
  );
};

export default AdminUserPanel;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  P09_PageContainer: {
    backgroundColor: kColors.white,
    flexGrow: 1,
    marginRight: 6 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
  },
  formControl: {
    marginBottom: 16,
  },
  gFormHeader: {
    ...kFonts.profileHeader,
    marginBottom: kStyleConst.kSpacing * 2,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gHeaderSpacing: {
    marginVertical: 15,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputGray: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    color: kColors.mediumGrey,
    flex: 1,
    height: 36,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gSpaceBetweenRow: {
    alignItems: "baseline",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  minWMd: {
    minWidth: 150,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  rowColumn: {
    flex: 1,
    marginBottom: 16,
    paddingEnd: 16,
  },
  rowNoMargin: {
    flexDirection: "row",
    marginTop: 0,
  },
});
